/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { useIsMutating } from "@tanstack/react-query";

// Component imports
import { Alert, Col, Row, Select, Skeleton, Typography } from "antd";
import { Container } from "@/components/UI/Container";
import { BackButton } from "@/components/UI/BackButton";
import { SubscriptionPlan } from "@/components/Subscriptions/SubscriptionPlan";
import { DataLoadingSkeleton } from "@/components/UI/DataLoadingSkeleton";
import { CancelSubscriptionModal } from "@/components/Subscriptions/CancelSubscriptionModal";

// Misc imports
import { useGetSubscriptionPlans } from "@/api/subscriptions";
import { ICurrency, IPendingSubscriptionAction, IPrice, ISubscriptionPlan } from "@/interfaces/SubscriptionPlans";
import { useGetProject } from "@/api/projects";
import { getFormattedDate } from "@/utils/date/getFormattedDate";
import { useUserInfo } from "@/api/user";
import { CURRENCY_SYMBOLS, NAV_PROJECT_SINGLE_VIEW, NAV_SUBSCRIPTIONS } from "@/constants";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";
import replaceUrlParams from "@/utils/string/replaceUrlParams";

const Option = Select;

/**
 * Component to display the subscription plans list and handle the creation of a new one
 * @returns JSX.Element
 */
export const SubscriptionPlans = (): JSX.Element => {
  const { t } = useTranslation();
  const { workspaceId, projectId } = useParams();
  const tokensInstance = Tokens;
  const location = useLocation();

  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};

  // How many mutations matching the posts prefix are fetching?
  const isUpdatingSubscription = useIsMutating({
    mutationKey: ["update-subscription"],
  });
  const params = {
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  };
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency>("eur");
  const [pendingSubscriptionAction, setPendingSubscriptionAction] = useState<IPendingSubscriptionAction | null>(null);

  const { data: subscriptionPlans, isLoading: isLoadingPlans } = useGetSubscriptionPlans();
  const { data: projectData, isLoading: isLoadingProjectData } = useGetProject(params);
  const { data: userInfoData, isFetching: isFetchingUserInfo } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);

  const projectTitle = projectData?.title || "";
  
  // USE-EFFECT
  useEffect(() => {
    if (userInfoData?.currency) {
      setSelectedCurrency(userInfoData?.currency);
    }
  }, [userInfoData?.currency]);

  const getDefaultPlanTitle = (): string | undefined => {
    return subscriptionPlans?.find((plan) => plan.is_default)?.title;
  };

  const isDefaultPlanActive = () => {
    return projectData?.subscription_plan === getDefaultPlanTitle();
  };

  const isPlanActive = (plan: ISubscriptionPlan): boolean => {
    return projectData?.subscription_plan.toLocaleLowerCase() === plan.title.toLocaleLowerCase();
  };

  // RENDER FUNCTIONS
  const renderCurrencySelector = () => {
    if (!subscriptionPlans) {
      return null;
    }
    return subscriptionPlans[1]?.prices?.map((priceObj: IPrice) => (
      <Option value={priceObj.currency}>{CURRENCY_SYMBOLS[priceObj.currency].title}</Option>
    ));
  };

  const getCancelAlertMessage = () => {
    const subscriptionPlan = projectData?.subscription_plan;
    const cancellationDate = getFormattedDate(projectData?.pending_subscription_cancellation_date, userInfoData?.timezone);

    const key = projectData?.is_trial ? "subscription_plans.trial_cancel_alert" : "subscription_plans.cancel_alert";

    return t(key, { subscription_plan: subscriptionPlan, cancellation_date: cancellationDate });
  };

  const renderSubscriptionPlans = () => {
    if ((!projectData && isLoadingProjectData) || (!subscriptionPlans && isLoadingPlans)) {
      // Show skeleton
      return <DataLoadingSkeleton />;
    }
    return (
      <>
        {projectData?.pending_subscription_cancellation_date && !projectData?.is_trial ? (
          <Alert showIcon type="warning" className="mb-4 p-3" message={getCancelAlertMessage()} />
        ) : null}

        <Row gutter={16}>
          {subscriptionPlans?.map((plan: ISubscriptionPlan) => (
            <Col className="mb-5" lg={6} md={12} xs={24} key={plan.title}>
              <SubscriptionPlan
                plan={plan}
                disableBtn={!!isUpdatingSubscription}
                hasBeenCancelled={!!projectData?.pending_subscription_cancellation_date}
                hasActiveSubscription={!isDefaultPlanActive()}
                isActive={isPlanActive(plan)}
                isTrial={projectData?.is_trial || false}
                selectedCurrency={selectedCurrency}
                setPendingSubscriptionAction={setPendingSubscriptionAction}
              />
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const renderCurrencyRelatedComponents = () => {
    if (isFetchingUserInfo || isLoadingPlans) {
      return <Skeleton active />;
    }
    if (userInfoData?.currency) {
      return null;
    }
    return (
      <>
        <label className="d-block mb-2">{t("subscription_plans.currency_label")}</label>
        <Select
          className="w-100 mb-3"
          onChange={(v) => setSelectedCurrency(v)}
          defaultValue={selectedCurrency}
          placeholder={t("common.select_option_placeholder")}
        >
          {renderCurrencySelector()}
        </Select>
      </>
    );
  };

  return (
    <Container>
      {/* Back Button to project list */}
      <Row>
        <Col xs={24}>
          <BackButton
            to={
              location.state?.canGoBack
                ? -1
                : replaceUrlParams(NAV_SUBSCRIPTIONS, {
                    workspaceId: workspaceId as string,
                    projectId: projectId as string,
                  })
            }
          />
        </Col>
      </Row>

      {/* ./ Back Button to project list */}
      <Row className="mb-2" align="middle">
        <Col xs={24} md={16} flex="auto">
          <Typography.Title className="page-title" level={2}>
            {t("subscription_plans.title")}
          </Typography.Title>
          <h4>
            <Trans i18nKey="subscription_plans.description" projectTitle={projectTitle}>
              Select one of the available plans for
              <Link
                to={replaceUrlParams(NAV_PROJECT_SINGLE_VIEW, {
                  workspaceId: workspaceId as string,
                  projectId: projectId as string,
                })}
              >
                {{ projectTitle } as unknown as string}
              </Link>
              , that best fits your needs.
            </Trans>
          </h4>
        </Col>

        <Col xs={24} md={8}>
          <div className="float-right">{renderCurrencyRelatedComponents()}</div>
        </Col>
      </Row>

      {renderSubscriptionPlans()}

      <CancelSubscriptionModal pendingSubscriptionAction={pendingSubscriptionAction} setPendingSubscriptionAction={setPendingSubscriptionAction} />
    </Container>
  );
};
